import { useState, useEffect, useRef } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SyncIcon from '@mui/icons-material/Sync';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LayersIcon from '@mui/icons-material/Layers';
import socket from '../../../config';

const Spinner = ({ size = 20, color = '#4A67FF' }) => (
    <SyncIcon style={{ fontSize: size, color, animation: 'spin 1s linear infinite' }} />
);

const getStatusIcon = (status) => {
    if (status === 'done') return (
        <div className="df aic jcc br100" style={{ width: 28, height: 28, backgroundColor: '#E6F9EE' }}>
            <CheckIcon style={{ fontSize: 14, color: '#09B255' }} />
        </div>
    );
    if (status === 'processing') return (
        <div className="df aic jcc br100" style={{ width: 28, height: 28, backgroundColor: '#EEF0FF' }}>
            <Spinner size={14} color="#4A67FF" />
        </div>
    );
    return (
        <div className="df aic jcc br100" style={{ width: 28, height: 28, backgroundColor: '#F5F6F9' }}>
            <RadioButtonUncheckedIcon style={{ fontSize: 10, color: '#D9D9D9' }} />
        </div>
    );
};

const ModuleRow = ({ mod, isLast }) => {
    const isDone = mod['status'] === 'done';
    const isProcessing = mod['status'] === 'processing';
    const pct = mod['totalSections'] > 0 ? Math.round((mod['sectionsDone'] / mod['totalSections']) * 100) : 0;

    return (
        <div className="df aic px1-5rem py1rem" style={{ borderBottom: isLast ? 'none' : '1px solid #F0F2FF', gap: 14 }}>
            <div style={{ flexShrink: 0 }}>{getStatusIcon(mod['status'])}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div className="df aic jcsb mb0-25rem">
                    <span className="fs13px fw500 oh" style={{ color: mod['status'] === 'pending' ? '#AAACB5' : '#151313', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {mod['moduleName']}
                    </span>
                    <span className="fs11px fw400 wsn" style={{ color: isDone ? '#09B255' : '#AAACB5', flexShrink: 0, marginLeft: 8 }}>
                        {isDone
                            ? `${mod['questionsGenerated']} issue${mod['questionsGenerated'] !== 1 ? 's' : ''} found`
                            : isProcessing
                                ? `${mod['sectionsDone']} / ${mod['totalSections']} sections`
                                : 'Waiting…'}
                    </span>
                </div>
                {(isProcessing || isDone) && (
                    <div style={{ height: 4, borderRadius: 4, backgroundColor: '#F0F2FD', overflow: 'hidden' }}>
                        <div style={{
                            height: '100%',
                            borderRadius: 4,
                            backgroundColor: isDone ? '#09B255' : '#4A67FF',
                            width: `${isDone ? 100 : pct}%`,
                            transition: 'width 0.4s ease',
                        }} />
                    </div>
                )}
            </div>
        </div>
    );
};

// Read saved progress from localStorage (shared key with WorkflowMasterSummary helpers)
const readSaved = (cid) => {
    if (!cid) return null;
    try {
        const raw = localStorage.getItem(`wf_progress_${cid}`);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (Date.now() - (data.startedAt || 0) > 15 * 60 * 1000) return null;
        return data;
    } catch { return null; }
};

const WorkflowGenerationProgress = ({ companyId, onComplete, onError }) => {
    const saved = readSaved(companyId);

    const [modules, setModules] = useState(saved?.modules || []);
    const [doneCount, setDoneCount] = useState((saved?.modules || []).filter(m => m.status === 'done').length);
    const [phase, setPhase] = useState(saved?.phase || 'waiting');
    const [errorMsg, setErrorMsg] = useState('');

    const onCompleteRef = useRef(onComplete);
    const onErrorRef = useRef(onError);
    const completedRef = useRef(false);
    // Use a ref so the persist helper inside the effect always has the current companyId
    const companyIdRef = useRef(companyId);

    useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
    useEffect(() => { onErrorRef.current = onError; }, [onError]);
    useEffect(() => { companyIdRef.current = companyId; }, [companyId]);

    useEffect(() => {
        const persist = (newModules, newPhase) => {
            const cid = companyIdRef.current;
            if (!cid) return;
            try {
                const raw = localStorage.getItem(`wf_progress_${cid}`);
                const existing = raw ? JSON.parse(raw) : {};
                localStorage.setItem(`wf_progress_${cid}`, JSON.stringify({ ...existing, phase: newPhase, modules: newModules }));
            } catch { }
        };

        const handleStarted = ({ modules: mods }) => {
            const initial = (mods || []).map(m => ({ ...m, sectionsDone: 0, status: 'pending', questionsGenerated: 0 }));
            setPhase('running');
            setDoneCount(0);
            setModules(initial);
            persist(initial, 'running');
        };

        const handleSectionDone = ({ moduleId, sectionsDone, totalSections }) => {
            setModules(prev => {
                const next = prev.map(m => {
                    if (String(m['moduleId']) !== String(moduleId)) return m;
                    const newStatus = sectionsDone >= totalSections ? 'done' : 'processing';
                    return { ...m, sectionsDone, totalSections, status: newStatus };
                });
                setDoneCount(next.filter(m => m.status === 'done').length);
                persist(next, 'running');
                return next;
            });
        };

        const handleModuleDone = ({ moduleId, questionsGenerated }) => {
            setModules(prev => {
                const next = prev.map(m =>
                    String(m['moduleId']) === String(moduleId)
                        ? { ...m, status: 'done', questionsGenerated }
                        : m
                );
                setDoneCount(next.filter(m => m.status === 'done').length);
                persist(next, 'running');
                return next;
            });
        };

        const handleComplete = () => {
            if (completedRef.current) return;
            completedRef.current = true;
            setPhase('done');
            setModules(prev => {
                const updated = prev.map(m => m['status'] !== 'done' ? { ...m, status: 'done' } : m);
                setDoneCount(updated.length);
                persist(updated, 'done');
                return updated;
            });
            setTimeout(() => onCompleteRef.current?.(), 1200);
        };

        const handleError = ({ message } = {}) => {
            setPhase('error');
            setErrorMsg(message || 'Generation failed. Please try again.');
            onErrorRef.current?.();
        };

        socket.on('workflow:started', handleStarted);
        socket.on('workflow:section:done', handleSectionDone);
        socket.on('workflow:module:done', handleModuleDone);
        socket.on('workflow:complete', handleComplete);
        socket.on('workflow:error', handleError);

        return () => {
            socket.off('workflow:started', handleStarted);
            socket.off('workflow:section:done', handleSectionDone);
            socket.off('workflow:module:done', handleModuleDone);
            socket.off('workflow:complete', handleComplete);
            socket.off('workflow:error', handleError);
        };
    }, []); // empty deps — refs keep all values current

    const totalCount = modules.length;

    const headerIcon = () => {
        if (phase === 'done') return (
            <div className="df aic jcc br100" style={{ width: 56, height: 56, backgroundColor: '#E6F9EE' }}>
                <CheckIcon style={{ fontSize: 26, color: '#09B255' }} />
            </div>
        );
        if (phase === 'error') return (
            <div className="df aic jcc br100" style={{ width: 56, height: 56, backgroundColor: '#FEE8E8' }}>
                <ErrorOutlineIcon style={{ fontSize: 26, color: '#dd4040' }} />
            </div>
        );
        return (
            <div className="df aic jcc br100" style={{ width: 56, height: 56, backgroundColor: '#EEF0FF' }}>
                <Spinner size={26} color="#4A67FF" />
            </div>
        );
    };

    const headerTitle = phase === 'done'
        ? 'Priority Manager Ready!'
        : phase === 'error'
            ? 'Generation Failed'
            : 'Generating Priority Manager…';

    const headerSub = phase === 'done'
        ? 'Your priority issues have been analysed. Loading your results…'
        : phase === 'error'
            ? errorMsg
            : phase === 'waiting'
                ? 'Preparing your modules for analysis…'
                : 'Analysing unanswered questions across your due diligence modules.';

    return (
        <>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <div className="sectionWrapper py1rem anylist-builder-tab df fdc aic" style={{ minHeight: 400, paddingTop: '3rem' }}>
                <div className="df fdc aic tac">
                    <div className="mb1rem">{headerIcon()}</div>
                    <h3 className="fs1-25rem fw600 c15 mb0-25rem">{headerTitle}</h3>
                    <p className="fs0-875rem fw400 c12" style={{ maxWidth: 460 }}>{headerSub}</p>
                </div>

                {totalCount > 0 && (
                    <div className="w100 bsbb" style={{ maxWidth: 580 }}>
                        <div className="bg1 br15px" style={{ border: '1px solid #DDE2F2', overflow: 'hidden' }}>
                            <div className="df aic px1-5rem py0-75rem" style={{ backgroundColor: '#F5F6F9', borderBottom: '1px solid #DDE2F2', gap: 6 }}>
                                <LayersIcon style={{ fontSize: 15, color: '#4A67FF' }} />
                                <span className="fs12px fw600 c15">Module Progress</span>
                                <span className="fs11px fw400" style={{ color: '#AAACB5' }}>{doneCount} / {modules.length} complete</span>
                            </div>
                            {modules.map((mod, idx) => (
                                <ModuleRow key={mod['moduleId']} mod={mod} isLast={idx === totalCount - 1} />
                            ))}
                        </div>
                    </div>
                )}

                {phase !== 'done' && phase !== 'error' && (
                    <p className="fs12px fw400 mt1rem tac" style={{ color: '#AAACB5' }}>
                        This may take a few minutes depending on the number of modules…
                    </p>
                )}
            </div>
        </>
    );
};

export default WorkflowGenerationProgress;
